"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * PILHA compartilhada por todos os consumidores deste hook (não por instância).
 *
 * Era um slot único, assumindo que nunca há dois abertos ao mesmo tempo. Deixou
 * de valer quando os DRAWERS entraram: o chat pode abrir sobre o carrinho, e o
 * segundo a registrar sobrescrevia o primeiro — a entrada dele ficava órfã no
 * histórico e um "voltar" depois saía da página sem fechar nada.
 *
 * Cada registro tem token próprio, então fechar por qualquer via remove
 * exatamente o seu, nunca o do vizinho. O voltar nativo consome sempre o TOPO,
 * que é o que está por cima na tela.
 *
 * Um único listener de `popstate`, registrado lazy na primeira abertura.
 * `close()` chamado pela própria UI (botão/link) fecha direto, sem esperar o
 * evento — que é assíncrono e só dispara quando o voltar é nativo.
 */
interface HistoryEntry {
    token: symbol;
    close: () => void;
}

const stack: HistoryEntry[] = [];
let popstateListenerAttached = false;
let nextEntryId = 0;

function ensurePopstateListener() {
    if (popstateListenerAttached) return;
    popstateListenerAttached = true;

    window.addEventListener("popstate", () => {
        stack.pop()?.close();
    });
}

function unregister(token: symbol) {
    const index = stack.findIndex((entry) => entry.token === token);
    if (index !== -1) stack.splice(index, 1);
}

/**
 * Conecta um modal/painel fullscreen mobile ao histórico nativo do
 * navegador (gesto de voltar do iOS, botão físico do Android), SEM tocar a
 * URL visível — só pushState com a mesma URL. Substitui
 * useMobileFullscreenHistory (removido): a diferença é o slot único
 * compartilhado acima, que garante que fechar por QUALQUER via (botão X,
 * clique num link de saída, voltar nativo) sempre consome a MESMA entrada
 * de history, nunca deixando uma entrada órfã — essa dessincronia entre a
 * pilha do navegador e o app era a causa do bug em que fechar o painel de
 * categorias podia levar a uma rota anterior errada.
 *
 * `isOpen` controla quando a entrada existe: ao virar `true`, empurra uma
 * entrada de history e registra `onClose` no slot (usado só pelo voltar
 * nativo). O cleanup do efeito só desfaz o registro em memória — o React
 * Strict Mode do dev roda mount→cleanup→remount de propósito, e um
 * `history.back()` no cleanup navegaria a página de verdade.
 *
 * Retorna `close`: os consumidores devem chamar ISSO (não `onClose`
 * diretamente) em botões/links de fechar. `close()` chama `onClose`
 * IMEDIATAMENTE (não espera o evento `popstate`, que é assíncrono e só
 * dispara de fato quando o voltar é nativo) — `history.back()` aqui serve
 * só para manter a pilha do navegador sincronizada, consumindo a entrada
 * que este hook empurrou.
 */
export function useModalHistoryEntry(isOpen: boolean, onClose: () => void) {
    // "Latest ref": guarda o onClose mais recente para o popstate e o close()
    // lerem sem entrar nas deps. Atualizado em efeito (após o commit), nunca
    // durante o render — os dois leitores só rodam em eventos, depois disso.
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });

    const tokenRef = useRef<symbol | null>(null);
    // Id gravado no `history.state` da entrada empurrada — é como o close()
    // sabe se ela ainda é a entrada ATUAL do navegador.
    const entryIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isOpen || tokenRef.current) return;

        ensurePopstateListener();
        const token = Symbol("modalHistoryEntry");
        tokenRef.current = token;
        const entryId = ++nextEntryId;
        entryIdRef.current = entryId;
        history.pushState({ modalHistoryEntry: entryId }, "");
        stack.push({
            token,
            close: () => {
                tokenRef.current = null;
                onCloseRef.current();
            },
        });

        return () => {
            // Só desfaz o registro em memória. O Strict Mode do dev roda
            // mount→cleanup→remount de propósito, e um `history.back()` aqui
            // navegaria a página de verdade.
            if (tokenRef.current) unregister(tokenRef.current);
            tokenRef.current = null;
        };
    }, [isOpen]);

    const close = useCallback(() => {
        const token = tokenRef.current;
        if (!token) {
            onCloseRef.current();
            return;
        }

        tokenRef.current = null;
        unregister(token);
        // Mantém a pilha do navegador em sincronia, consumindo a entrada que
        // este hook empurrou — mas SÓ se ela ainda for a atual. O chat
        // continua aberto enquanto o usuário navega (link de produto dentro
        // dele, troca de variação na PDP): aí a entrada ficou para trás, e um
        // `history.back()` desfaria a navegação, voltando à página anterior.
        if ((history.state as { modalHistoryEntry?: number } | null)?.modalHistoryEntry === entryIdRef.current) {
            history.back();
        }
        entryIdRef.current = null;
        onCloseRef.current();
    }, []);

    return close;
}
